/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Modal, message, Input, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { reportingApi } from '@/services/reporting/reporting-api';
import { RequestDTO } from '@/types/requestDTO';
import { useAuthStore } from '@/store/useAuthStore';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';
import { createUnitHelpers } from '@/components/MeasureIndicators/helpers/unitHelpers';
import FinderButton from '@/components/common/FinderButton';
import CustomTooltip from '@/components/common/Tooltip';
import CommonJs from '@/utils/commonJs';
import {
  appendStaticFileAuthParams,
  extractFileNameFromUrl,
  formatAttachmentDisplayLabel,
} from '@/components/common/FileAndLinkUpload';
import { ModalPheDuyetBaoCaoReportApproveCell } from './ModalPheDuyetBaoCaoReportApproveCell';
import { toSafeHtml } from '@/utils/sanitizeHtml'
interface ApproveModalProps {
  data: any[];
  loading?: boolean;
}

/**
 * Modal content hiển thị danh sách báo cáo KPI cần phê duyệt.
 * Dựa trên UI cũ trong _getmodalpheduyetbaocao.cshtml nhưng đơn giản hóa cho React.
 */
export const GetModalPheDuyetBaoCao: React.FC<ApproveModalProps> = ({ data, loading }) => {
  const { t } = useTranslation();
  const { tenantID, profile } = useAuthStore();
  const currentUserID = String(profile?.id ?? '');
  const { units, getEmployeeOrDepartmentFullName } = useLocalFilter();
  const unitHelpers = useMemo(() => createUnitHelpers(units, tenantID || ''), [units, tenantID]);
  const [filesByRow, setFilesByRow] = useState<Record<string, string[]>>({});

  const rowsCanApprove = useMemo(
    () =>
      (data ?? []).filter(
        (row: any) =>
          row.baoCao?.guiDuyet &&
          Number(row.baoCao?.tinhTrangDuyet) === 0 &&
          String(row.baoCao?.nhanVienDuyet ?? '') === currentUserID
      ),
    [data, currentUserID]
  );

  const handleApproveAll = useCallback(
    (status: 1 | 2) => {
      if (rowsCanApprove.length === 0) {
        message.info(t('bsc.msg.khongcobaocaodetxetduyet', 'Không có báo cáo nào để xét duyệt'));
        return;
      }
      const runAll = async (ghiChu?: string) => {
        for (const row of rowsCanApprove) {
          const baoCaoID = String(row.baoCaoID || row.BaoCaoID || row.idBaoCao || row.IDBaoCao || '');
          const chiTieuID = String(row.chiTieuID || row.ChiTieuID || row.id || row.ID || '');
          if (!baoCaoID || !chiTieuID) continue;
          try {
            const response: any = await reportingApi.approvePheDuyetBaoCao({
              id: chiTieuID,
              idBaoCao: baoCaoID,
              phanHe: 'bsc',
              site: tenantID || '',
              status,
              ghiChu: ghiChu ?? '',
            });
            if (response?.status !== true) {
              message.error(response?.msg ?? t('bsc.msg.loipheduyetbaocao_id', 'Lỗi khi phê duyệt báo cáo {{id}}', { id: baoCaoID }));
            }
          } catch (e) {
            console.error('approvePheDuyetBaoCao failed', e);
            message.error(t('bsc.msg.loipheduyetbaocao', 'Có lỗi xảy ra khi phê duyệt báo cáo'));
          }
        }
        message.success(status === 1 ? t('bsc.msg.daxetduyettatca', 'Đã xét duyệt tất cả') : t('bsc.msg.datuchoitatca', 'Đã từ chối tất cả'));
        try {
          window.dispatchEvent(new CustomEvent('report-kpis:refresh-list'));
          window.dispatchEvent(new CustomEvent('noti-baobaokpi'));
        } catch { }
      };
      const ghiChuRef = { current: '' };
      const content = (
        <div>
          <p style={{ marginBottom: 8 }}>
            {status === 1
              ? t('bsc.msg.xacnhanxetduyettatca', 'Bạn có chắc xét duyệt tất cả ({{count}}) báo cáo?', { count: rowsCanApprove.length })
              : t('bsc.msg.xacnhantuchoitatca', 'Bạn có chắc từ chối tất cả ({{count}}) báo cáo?', { count: rowsCanApprove.length })}
          </p>
          <Input.TextArea
            placeholder={t('bsc.msg.vuilongnhaplydo', 'Vui lòng nhập lý do...')}
            rows={2}
            onChange={(e) => { ghiChuRef.current = e.target.value; }}
          />
        </div>
      );
      const onOk = () => {
        const ghiChu = (ghiChuRef.current ?? '').trim();
        if (!ghiChu) {
          message.error(t('bsc.msg.vuilongnhaplydo', 'Vui lòng nhập lý do...'));
          return Promise.reject();
        }
        return runAll(ghiChu);
      };
      if (status === 1) {
        Modal.confirm({
          title: t('bsc.btn.xacnhan', 'Xác nhận'),
          content,
          okText: t('bsc.btn.xacnhan', 'Xác nhận'),
          cancelText: t('bsc.btn.huy', 'Hủy'),
          centered: true,
          onOk,
        });
      } else {
        Modal.confirm({
          title: t('bsc.btn.xacnhan', 'Xác nhận'),
          content,
          okText: t('bsc.btn.xacnhan', 'Xác nhận'),
          cancelText: t('bsc.btn.huy', 'Hủy'),
          centered: true,
          onOk,
        });
      }
    },
    [rowsCanApprove, tenantID, t]
  );

  const columns = useMemo(
    () => [
        {
          title: t('bsc.label.tenkpi', 'Tên KPIs'),
          dataIndex: 'tenKPI',
          key: 'tenKPI',
          width: 260,
          render: (_: any, row: any, index: number) => {
            const tenkpi = row.tenKPI || '';
            return (
              <div style={{ fontSize: 13, fontWeight: 600, textAlign: 'left' }}>
                <span>{index + 1}. </span>
                <span>{tenkpi}</span>
              </div>
            );
          },
        },
        {
          title: t('bsc.label.dinhnghia', 'Định nghĩa'),
          dataIndex: 'dinhNghia',
          key: 'dinhNghia',
          width: 220,
          render: (_: any, row: any) => (
            <div style={{ fontSize: 13 }} dangerouslySetInnerHTML={toSafeHtml(row.dinhNghia || '')} />
          ),
        },
        {
          title: t('bsc.label.donvi', 'Đơn vị'),
          dataIndex: ['chiTieu', 'idDoLuong'],
          key: 'donVi',
          width: 150,
          render: (_: any, row: any) => {
            const unitLabel = row.chiTieu?.idDoLuong ? unitHelpers.getUnitLabel(row.chiTieu?.idDoLuong) : '';
            return (
              <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                <div style={{ fontWeight: 600 }}>{unitLabel}</div>
              </div>
            );

          },
        },
        {
          title: t('bsc.label.phutrach', 'Phụ trách'),
          dataIndex: 'phuTrach',
          key: 'phuTrach',
          width: 160,
          render: (_: any, row: any) => {
            // Dùng boPhanDanhGia để lấy tên bộ phận / nhân sự giống KPIInfoTable
            const boPhanId = row.chiTieu?.boPhanDanhGia || row.chiTieu?.idPhuTrach || '';
            const fullName = boPhanId ? getEmployeeOrDepartmentFullName(boPhanId) : '';
            return <span style={{ fontSize: 13, fontWeight: 600 }}>{fullName}</span>;
          },
        },
        {
          title: t('bsc.label.baocao', 'Báo cáo'),
          dataIndex: 'kyBaoCao',
          key: 'kyBaoCao',
          width: 160,
          render: (_: any, row: any) => {
            const baoCao = row.baoCao || {};
            const tinhTrang = baoCao.tinhTrang || '';
            const kyBaoCao = row.kyBaoCao || '';
            return (
              <div style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#0c68e9' }}>{tinhTrang}</div>
                <div style={{ fontWeight: 400 }}>{kyBaoCao}</div>
              </div>
            );
          },
        },
        {
          title: t('bsc.label.thucte', 'Thực tế'),
          dataIndex: 'thucTe',
          key: 'thucTe',
          width: 120,
          render: (_: any, row: any) => {
            const checkKPIChoice = Boolean(String(row.chiTieu?.tiLeKPIChoices || '').trim());
            var thucte = '';
            if (row.dabaocao) {
              thucte = row.baoCao.thucTe
            }
            if (row.baoCao.tinhTrangDuyet == 0) {
              thucte = row.baoCao.thucTe_Temp != null ? row.baoCao.thucTe_Temp : '';
            }
            var checkedit = (row.baoCao.nhanVienDuyet == currentUserID && row.baoCao.tinhTrangDuyet != 1) || (row.checkDanhGia && row.baoCao.tinhTrangDuyet == 2);

            if (unitHelpers.isThangDoUnit(row.chiTieu?.idDoLuong)) {
              const thucTeValue = String(thucte ?? '');
              var currentLabel = unitHelpers.getChildUnitLabel(row.chiTieu?.idDoLuong, thucTeValue);
              return (
                <Select
                  disabled={checkKPIChoice || !checkedit}
                  defaultValue={currentLabel}
                  style={{ width: '100%', textAlign: 'right', fontWeight: 600, opacity: 1 }}
                  options={unitHelpers.getUnitChildren(row.chiTieu?.idDoLuong).map((child: any) => ({
                    label: child.label,
                    value: child.label,
                  }))}
                  className="unit-select-bold disabled-no-opacity"
                  suffixIcon={null}
                />
              );
            } else {

              return (
                <Input.TextArea
                  disabled={checkKPIChoice || !checkedit}
                  defaultValue={thucte != null && thucte !== '' ? CommonJs.formatMoney(thucte) : ''}
                  autoSize
                  className="font-bold disabled-no-opacity color-black"
                  style={{ color: 'black', resize: 'none', fontSize: 13, textAlign: 'right', fontWeight: 600 }}>
                  {thucte != null && thucte !== '' ? CommonJs.formatMoney(thucte) : ''}
                  </Input.TextArea>
              );
            }

          },
        },
        {
          title: t('bsc.label.danhgia', 'Đánh giá'),
          dataIndex: 'danhGia',
          key: 'danhGia',
          width: 220,
          render: (val: any, row: any) => {
            const baoCao = row.baoCao || {};
            const idBaoCao = String(row.baoCaoID || row.BaoCaoID || baoCao.id || baoCao.ID || '');
            const idChiTieu = String(row.chiTieuID || row.ChiTieuID || row.chiTieu?.id || '');
            const initialValue = val || baoCao.danhGia || '';

            // debounce giống tableColumns.tsx
            const key = `${idChiTieu}__${idBaoCao}`;
            if (!(window as any).__nhapDanhGiaTimers) {
              (window as any).__nhapDanhGiaTimers = new Map<string, any>();
            }

            return (
              <Input.TextArea

                defaultValue={initialValue}
                autoSize
                style={{ background: 'transparent', resize: 'none', fontSize: 12, textAlign: 'left' }}
                onChange={(e) => {
                  const timers: Map<string, any> = (window as any).__nhapDanhGiaTimers;
                  const prev = timers.get(key);
                  if (prev) {
                    clearTimeout(prev);
                  }
                  const next = setTimeout(async () => {
                    try {
                      await reportingApi.nhapDanhGia({
                        id: idChiTieu,
                        idBaoCao: idBaoCao,
                        noiDung: e.target.value,
                        phanHe: 'bsc',
                      } as any);
                      message.success(t('bsc.msg.capnhatdanhgiathanhcong', 'Cập nhật đánh giá thành công'));
                    } catch (err) {
                      console.error('nhapDanhGia failed', err);
                      message.error(t('bsc.msg.capnhatthatbai2', 'Cập nhật thất bại!'));
                    }
                  }, 500);
                  timers.set(key, next);
                }}
              />
            );
          },
        },
        {
          title: t('bsc.label.tailieu', 'Tài liệu'),
          dataIndex: 'filedinhkem',
          key: 'filedinhkem',
          width: 220,
          render: (_: any, row: any, index: number) => {
            const rawString = String(row.filedinhkem || '').trim();
            const rowId = String(
              row.baoCaoID || row.BaoCaoID || row.idBaoCao || row.IDBaoCao || row.id || row.ID || index
            );

            const parseFiles = (val: string): string[] =>
              val
                .split(',')
                .map((f: string) => f.trim())
                .filter((f: string) => f);

            const currentFiles = filesByRow[rowId] ?? (rawString ? parseFiles(rawString) : []);

            const handleUpload = async (selectedFiles: any[]) => {
              if (!selectedFiles || selectedFiles.length === 0) return;

              const newFiles = selectedFiles
                .map((file: any) => `${file.baseUrl}${file.viewUrl}`)
                .filter((u: string) => Boolean(String(u).trim()));

              const merged = [...currentFiles, ...newFiles].filter((u: string) => Boolean(String(u).trim()));
              const seen = new Set<string>();
              const deduped = merged.filter((u: string) => {
                if (seen.has(u)) return false;
                seen.add(u);
                return true;
              });

              setFilesByRow((prev) => ({ ...prev, [rowId]: deduped }));

              try {
                const baoCaoID = String(row.baoCaoID || row.BaoCaoID || row.idBaoCao || row.IDBaoCao || '');
                const chiTieuID = String(row.chiTieuID || row.ChiTieuID || row.id || row.ID || '');
                if (!baoCaoID || !chiTieuID) return;

                await reportingApi.uploadFile({
                  ID: chiTieuID,
                  IDBaoCao: baoCaoID,
                  ListTenFile: deduped,
                  phanHe: 'bsc',
                } as any);
                message.success(t('bsc.msg.capnhatthanhcongbaocaokpi', 'Cập nhật thành công!'));
                try {
                  window.dispatchEvent(new CustomEvent('noti-baobaokpi'));
                } catch { }
              } catch (err) {
                console.error('uploadFile failed', err);
                message.error(t('bsc.msg.tailenhthatbai', 'Tải tệp thất bại'));
              }
            };

            const handleDelete = async (fileUrl: string, fileIndex: number) => {
              const source = filesByRow[rowId] ?? currentFiles;
              const nextFiles = source.filter((_: string, idx: number) => idx !== fileIndex);
              setFilesByRow((prev) => ({ ...prev, [rowId]: nextFiles }));

              try {
                const baoCaoID = String(row.baoCaoID || row.BaoCaoID || row.idBaoCao || row.IDBaoCao || '');
                const chiTieuID = String(row.chiTieuID || row.ChiTieuID || row.id || row.ID || '');
                if (!baoCaoID || !chiTieuID) return;

                await reportingApi.uploadFile({
                  ID: chiTieuID,
                  IDBaoCao: baoCaoID,
                  ListTenFile: nextFiles.filter((u: string) => u !== fileUrl),
                  phanHe: 'bsc',
                } as any);
                message.success(t('bsc.msg.xoabaoCaoThanhCong', 'Xóa báo cáo thành công!'));
                try {
                  window.dispatchEvent(new CustomEvent('noti-baobaokpi'));
                } catch { }
              } catch (err) {
                console.error('removeFileBaoCao failed', err);
              }
            };

            return (
              <>
                <FinderButton type="file" multiple onSelect={handleUpload} />
                {currentFiles.length > 0 && (
                  <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {currentFiles.map((fileUrl: string, fileIndex: number) => {
                      const decodedRaw = (() => {
                        try {
                          return decodeURIComponent(fileUrl);
                        } catch {
                          return fileUrl;
                        }
                      })();
                      const baseName = extractFileNameFromUrl(decodedRaw);
                      const displayName = formatAttachmentDisplayLabel(decodedRaw);
                      const decoded = appendStaticFileAuthParams(decodedRaw);
                      return (
                        <div
                          key={fileIndex}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: '#f0f7ff',
                            gap: 10,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <CustomTooltip value={decodedRaw} placement="top" title={t('bsc.label.tenfile', 'Tên file')}>
                              <a
                                href={decoded}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'block',
                                  maxWidth: 300,
                                  color: 'rgb(84 113 208)',
                                  textDecoration: 'none',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {displayName}
                              </a>
                            </CustomTooltip>
                          </div>
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(fileUrl, fileIndex)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          },
        },
        {
          title: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {rowsCanApprove.length > 0 ? (
                <>
                  <span
                    role="button"
                    tabIndex={0}
                    className="btnDuyetAll"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      marginRight: 10,
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1,
                      minHeight: 24,
                      backgroundColor: '#28a745',
                      color: '#fff',
                    }}
                    onClick={() => handleApproveAll(1)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApproveAll(1)}
                  >
                    Duyệt tất cả
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="btnDuyetAll"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1,
                      minHeight: 24,
                      backgroundColor: '#dc3545',
                      color: '#fff',
                    }}
                    onClick={() => handleApproveAll(2)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApproveAll(2)}
                  >
                    {t('bsc.btn.khongduyettatca', 'Không duyệt tất cả')}
                  </span>
                </>
              ) : (
                <span>{t('vanhoa.tinhtrang', 'Tình trạng')}</span>
              )}
            </div>
          ),
          key: 'status',
          fixed: 'right' as const,
          width: 300,
          align: 'center' as const,

          render: (_: any, row: any) => {
            const baoCao = row.baoCao || {};
            const baoCaoID = String(row.baoCaoID || row.BaoCaoID || row.idBaoCao || row.IDBaoCao || baoCao.idbaocao || baoCao.IDBaoCao || '');
            const chiTieuID = String(row.chiTieuID || row.ChiTieuID || row.id || row.ID || '');
            const guiDuyet = Boolean(baoCao.guiDuyet);
            const tinhTrangDuyet = Number(baoCao.tinhTrangDuyet);
            const nhanVienDuyet = baoCao.nhanVienDuyet;
            const tinhTrang = baoCao.tinhTrang;
            const capDoDuyet = Number(baoCao.capDoDuyet);
            const ngay_Temp = baoCao.ngay_Temp;
            const dabaocao = Boolean(row.dabaocao);
            const recordForCell: any = {
              __raw: {
                idbaocao: baoCaoID,
                guiDuyet,
                tinhTrangDuyet,
                nhanVienDuyet,
                tinhTrang,
                capDoDuyet,
                ngay_Temp,
                dabaocao,
              },
              detail: {
                chiTieuNam: { id: chiTieuID },
              },
            };
            return <ModalPheDuyetBaoCaoReportApproveCell record={recordForCell} userID={currentUserID} />;
          },
        },
      ],
    [
      t,
      unitHelpers,
      getEmployeeOrDepartmentFullName,
      filesByRow,
      rowsCanApprove,
      currentUserID,
      handleApproveAll,
    ]
  );

  return (
    <Table
      size="small"
      rowKey={(row: any) => String(row.baoCaoID || row.BaoCaoID || row.id || row.ID || Math.random())}
      dataSource={data}
      pagination={false}
      columns={columns}
      locale={{
        emptyText: (
          <div style={{ padding: 16, textAlign: 'center' }}>{t('bsc.table.empty_khongcobaocaocanpheduyet', 'Không có báo cáo nào cần phê duyệt.')}</div>
        ),
      }}
    />
  );
};

export default GetModalPheDuyetBaoCao;

